import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Phone,
  FileText,
  BarChart3,
  Users,
  Menu,
  X,
  Radio,
  LogOut,
  User
} from 'lucide-react';

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  const navigationItems = [
    { path: '/', label: 'Dashboard', icon: Home },
    { path: '/call-management', label: 'Call Management', icon: Phone },
    { path: '/connections', label: 'Connections', icon: Users },
    { path: '/transcriptions', label: 'Transcriptions', icon: FileText },
    { path: '/summarizations', label: 'Summarizations', icon: BarChart3 },
  ];

  const isActivePath = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    // Clear authentication token
    localStorage.removeItem('auth_token');
    localStorage.removeItem('authToken');
    
    // Navigate to login page
    navigate('/login');
    
    // Close user menu
    setShowUserMenu(false);
  };

  const isAuthenticated = () => {
    return localStorage.getItem('auth_token') || localStorage.getItem('authToken');
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <nav className="nav">
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '64px' }}>
          {/* Logo and brand */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Link to="/" className="nav-brand" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
              }}>
                <Radio style={{ width: '18px', height: '18px', color: 'white' }} />
              </div>
              <span style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', whiteSpace: 'nowrap' }}>
                Audio Streaming Service
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} className="hidden-mobile">
            {navigationItems.map((item) => {
              const IconComponent = item.icon;
              const isActive = isActivePath(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link ${isActive ? 'active' : ''}`}
                >
                  <IconComponent style={{ width: '16px', height: '16px' }} />
                  <span>{item.label}</span>
                </Link>
              );
            })}

            {/* User Menu */}
            <div ref={userMenuRef} style={{ position: 'relative', marginLeft: '16px' }}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                style={{ 
                  padding: '8px 12px', 
                  background: isAuthenticated() ? '#dcfce7' : '#fef2f2', 
                  color: isAuthenticated() ? '#166534' : '#dc2626', 
                  borderRadius: '8px', 
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  border: 'none',
                  outline: 'none'
                }}
                title={isAuthenticated() ? "User Menu" : "Not Authenticated"}
              >
                <User style={{ width: '16px', height: '16px' }} />
                <span>{isAuthenticated() ? 'User' : 'Not Signed In'}</span>
                <div style={{ 
                  width: '6px', 
                  height: '6px', 
                  borderRadius: '50%', 
                  background: isAuthenticated() ? '#10b981' : '#dc2626'
                }}></div>
              </button>

              {/* User Dropdown Menu */}
              {showUserMenu && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: '0',
                  marginTop: '4px',
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  minWidth: '180px',
                  zIndex: 50
                }}>
                  <div style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #e2e8f0',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    Authentication Status
                  </div>
                  
                  <div style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    color: '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{ 
                      width: '8px', 
                      height: '8px', 
                      borderRadius: '50%', 
                      background: isAuthenticated() ? '#10b981' : '#dc2626'
                    }}></div>
                    {isAuthenticated() ? 'Authenticated' : 'Not Authenticated'}
                  </div>

                  {isAuthenticated() && (
                    <button
                      onClick={handleLogout}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'transparent',
                        border: 'none',
                        borderTop: '1px solid #e2e8f0',
                        fontSize: '14px',
                        color: '#dc2626',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'background-color 0.2s ease',
                        borderRadius: '0 0 8px 8px'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#fef2f2'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    >
                      <LogOut style={{ width: '16px', height: '16px' }} />
                      Sign Out
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="mobile-only">
            <button
              onClick={toggleMobileMenu}
              style={{
                background: 'none',
                border: 'none',
                color: '#64748b',
                cursor: 'pointer',
                padding: '8px'
              }}
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? (
                <X style={{ width: '24px', height: '24px' }} />
              ) : (
                <Menu style={{ width: '24px', height: '24px' }} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="mobile-only" style={{
            paddingTop: '16px',
            paddingBottom: '16px',
            borderTop: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {navigationItems.map((item) => {
                const IconComponent = item.icon;
                const isActive = isActivePath(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`nav-link ${isActive ? 'active' : ''}`}
                  >
                    <IconComponent style={{ width: '16px', height: '16px' }} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @media (max-width: 768px) {
          .hidden-mobile {
            display: none !important;
          }
        }

        @media (min-width: 769px) {
          .mobile-only {
            display: none !important;
          }
        }
      `}</style>
    </nav>
  );
};

export default Navigation; 